import React from 'react';
import { RiInformationLine } from '@remixicon/react';
import { MainButton } from '../shared/MainButton';
import { Order, TicketStatus, TicketType } from '@/lib/OrderApi';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../shared/Accordion';
import { useLanguage } from '@/providers/LanguageProvider';
import * as m from '@/paraglide/messages.js';

/**
 * FAS-974 — explains personalisation, sealed tickets, sharing and the marketplace on the
 * order page itself. The order page previously assumed the customer already knew all of
 * it; the confirmation mail said nothing about any of it either.
 *
 * The copy leads with what the customer gains rather than with "to prevent resale"
 * (robert, 25-08): being told your ticket is locked down reads as a restriction, and it
 * is the exact objection people have when they cannot use an outside resale site. It
 * says plainly that reselling IS possible — safely, through the event's own marketplace.
 * Sharing got its own block in the same pass: it is the most-used action on this page
 * and was the one subject the explanation never mentioned.
 *
 * Collapsed by default so it does not push the tickets down the page — the per-ticket
 * action lines carry the urgent part, this is the background reading.
 */

interface Props {
    order: Order;
}

/** The earliest release moment across the order's sealed tickets, if any. */
function sealedReleaseDate(order: Order, lang: string): string | null {
    const moments = order.tickets
        .filter(t => 'sealed' in t && t.sealed && 'availableFrom' in t && t.availableFrom)
        .map(t => new Date((t as { availableFrom: string }).availableFrom.split('[')[0]))
        .filter(d => !Number.isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());

    if (moments.length === 0) {
        return null;
    }

    return moments[0].toLocaleString(lang, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

const PersonalisationExplainer: React.FC<Props> = ({ order }) => {
    const { currentLanguage: lang } = useLanguage();

    const hasPersonalisedTickets = order.tickets.some(
        t => t.ticketMustBePersonalized
            && 'ticketType' in t
            && t.ticketType !== TicketType.GroupedTicket
            && t.ticketType !== TicketType.DonationTicket,
    );
    // FAS-1213 (killian, 04-09-2026) — the block below promised "sharing takes a few
    // clicks" on a page where sharing is impossible. A ticket you RECEIVED may be handed
    // back but not passed on again, so the API returns canShare: false and Ticket.tsx
    // renders no Share button — leaving an explanation of a button that is not there.
    // Gate it on the same facts the button uses, so the two cannot drift apart.
    const canShareAny = order.tickets.some(
        t => 'ticketType' in t
            && t.ticketType !== TicketType.GroupedTicket
            && t.ticketType !== TicketType.DonationTicket
            && (!('canShare' in t) || t.canShare !== false)
            && t.ticketMustBePersonalized
            && (t.ticketStatus === TicketStatus.claimable || t.ticketStatus === TicketStatus.available),
    );
    const sealedOn = sealedReleaseDate(order, lang);
    const canResellAny = order.marketplaceUrl !== null
        && order.tickets.some(t => 'canResell' in t && t.canResell === true);

    // Nothing to explain for an order of plain, non-personalised tickets.
    if (!hasPersonalisedTickets && sealedOn === null && !canResellAny) {
        return null;
    }

    const eventName = order.event.name;

    return (
        <div className="bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] rounded-md px-4 sm:px-8 py-2 mt-4">
            <Accordion type="single" collapsible>
                <AccordionItem value="explain">
                    {/* Shrink the shared trigger's 40px chevron to match this heading. */}
                    <AccordionTrigger canCollapse className="text-lg font-semibold [&>svg]:h-5 [&>svg]:w-5">
                        <span className="flex items-center gap-2">
                            <RiInformationLine size={20} />
                            {m.explain_title()}
                        </span>
                    </AccordionTrigger>
                    <AccordionContent>
                        <div className="space-y-4">
                            {hasPersonalisedTickets && (
                                <section>
                                    <h4 className="font-semibold mb-1">{m.explain_why_title()}</h4>
                                    <p className="text-sm tracking-tight">{m.explain_why_body()}</p>
                                </section>
                            )}

                            {sealedOn !== null && (
                                <section>
                                    <h4 className="font-semibold mb-1">{m.explain_sealed_title()}</h4>
                                    <p className="text-sm tracking-tight">
                                        {m.explain_sealed_body({ date: sealedOn })}
                                    </p>
                                </section>
                            )}

                            {canShareAny && (
                                <section>
                                    <h4 className="font-semibold mb-1">{m.explain_share_title()}</h4>
                                    <p className="text-sm tracking-tight">{m.explain_share_body()}</p>
                                </section>
                            )}

                            {canResellAny && order.marketplaceUrl !== null && (
                                <section>
                                    <h4 className="font-semibold mb-1">{m.explain_marketplace_title()}</h4>
                                    <p className="text-sm tracking-tight">
                                        {m.explain_marketplace_body({ eventName })}
                                    </p>
                                    <p className="text-sm tracking-tight mt-1 italic">
                                        {m.explain_marketplace_payout()}
                                    </p>
                                    <MainButton
                                        url={order.marketplaceUrl}
                                        newTab
                                        className="mt-3 w-full sm:w-auto sm:inline-flex"
                                    >
                                        {m.explain_marketplace_button({ eventName })}
                                    </MainButton>
                                    <p className="text-xs tracking-tight mt-2 opacity-80">
                                        {m.explain_marketplace_fraud()}
                                    </p>
                                </section>
                            )}
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    );
};

export default PersonalisationExplainer;
